import type { QueryKey, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

export type ExtendedQueryOptions<TQueryFnData, TError, TData = TQueryFnData> = UseQueryOptions<
  TQueryFnData,
  TError,
  TData
> & {
  onSuccess?: (data: TData) => void;
  onError?: (error: TError) => void;
  onSettled?: (data: TData | undefined, error: TError | null) => void;
};

export function useQueryWithCallbacks<TQueryFnData, TError = unknown, TData = TQueryFnData>(
  options: ExtendedQueryOptions<TData, TError>,
): UseQueryResult<TData, TError> {
  'use memo';
  const { onSuccess, onError, onSettled, ...queryOptions } = options;

  const query = useQuery<TData, TError, TData, QueryKey>(queryOptions);

  // Track whether we've already fired for this "fetch cycle"
  const hasFiredRef = useRef(false);
  const prevStatus = useRef(query.status);

  useEffect(() => {
    const status = query.status;
    const data = query.data;
    const error = query.error;
    const isFetching = query.isFetching;

    // Reset when starting a new fetch cycle
    if (isFetching || status === 'pending') {
      hasFiredRef.current = false;
    }

    // Only fire after the fetch settles (not mid-fetch)
    if (!isFetching && !hasFiredRef.current) {
      if (status === 'success') {
        onSuccess?.(data!);
        onSettled?.(data, null);
        hasFiredRef.current = true;
      } else if (status === 'error') {
        onError?.(error!);
        onSettled?.(undefined, error ?? null);
        hasFiredRef.current = true;
      }
    }

    prevStatus.current = status;
  }, [query.status, query.isFetching, query.data, query.error, onSuccess, onError, onSettled]);

  return query;
}
